<template>
  <BaseModal
    :name="UNLOCK_FULL_ACCESS_MODAL_NAME"
    :width-class="modalWidthClass"
    @hide="emit('hide')"
    @show="emit('show')"
  >
    <div v-if="isPlansModal" class="p-5 sm:p-8">
      <!-- A card of the mentor chat: the request the chat wrote waits for
           the plan. The strip of the sign-up page, for a referral. -->
      <div
        v-if="modalState?.type === 'chat' && modalState.avatarUrl"
        class="bg-secondary-50 text-secondary-800 mx-auto mb-5 flex max-w-xl items-center gap-3 rounded-xl p-3 pr-10 text-sm sm:pr-3"
        test_id="unlock-modal-chat-request"
      >
        <Avatar :src="modalState.avatarUrl" size="sm" class="shrink-0" />
        <span>
          Your request to
          <b>{{ modalState.firstName }}</b>
          goes out in the next step, after you pick a plan.
        </span>
      </div>
      <div class="text-center">
        <h2
          class="mx-auto max-w-xl text-3xl leading-tight font-bold tracking-tight text-neutral-900 sm:text-4xl"
        >
          One membership. Every mentor. Unlimited calls.
        </h2>
        <p
          class="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-neutral-600"
        >
          {{ plansLine }}
        </p>
      </div>

      <ul
        class="mx-auto mt-5 flex max-w-xl flex-wrap justify-center gap-x-5 gap-y-1.5 text-sm text-neutral-700"
      >
        <li
          v-for="benefit in roadmapBenefits"
          :key="benefit"
          class="flex items-center gap-2"
        >
          <span class="size-1.5 shrink-0 rounded-full bg-neutral-300" />
          {{ benefit }}
        </li>
      </ul>

      <div class="mt-6 flex justify-center">
        <BillingTermCard
          :personal-plans="unlockModalData?.personalPlans"
          :is-pending="purchasing"
          purchase-label="Get started"
          @purchase="checkout"
        />
      </div>
    </div>

    <div v-else class="p-6">
      <div class="flex flex-col items-center justify-center">
        <template v-if="modalState && modalState.type === 'request-a-call'">
          <Avatar :src="modalState.avatarUrl" size="2xl" />
          <div class="mt-6 text-center text-xl font-semibold">
            Connect with {{ modalState.firstName }} and {{ totalMentors }}+
            mentors
          </div>
          <div class="mt-1 text-center text-neutral-600">
            Get unstuck faster with unlimited mentorship calls
          </div>
        </template>

        <!-- A visitor's click on a card of the mentor chat: the chat
             already wrote the request, and the account keeps it. -->
        <template
          v-else-if="
            modalState &&
            modalState.type === 'chat-account' &&
            accountStep === 1
          "
        >
          <Avatar :src="modalState.avatarUrl" size="2xl" />
          <template v-if="modalState.action">
            <div class="mt-6 text-center text-xl font-semibold">
              {{ ACTION_TITLES[modalState.action] }} {{ modalState.firstName
              }}{{ modalState.action === 'list' ? ' to a list' : '' }}
            </div>
            <div class="mt-1 text-center text-neutral-600">
              First, make your account. It takes a minute.
            </div>
          </template>
          <template v-else>
            <div class="mt-6 text-center text-xl font-semibold">
              Your request to {{ modalState.firstName }} is written
            </div>
            <div class="mt-1 text-center text-neutral-600">
              Make your account and it waits for you in this chat. You read it
              and send it in the next step.
            </div>
          </template>
        </template>

        <!-- The slides after the first screen (ChatAccountSlide). -->
        <ChatAccountSlide
          v-else-if="modalState && modalState.type === 'chat-account'"
          :step="accountStep"
          :first-call="!!modalState.action"
          :mentors="
            modalState.mentors ?? [
              {
                id: 0,
                name: modalState.mentorName ?? modalState.firstName,
                title: modalState.mentorTitle ?? null,
                avatarUrl: modalState.avatarUrl,
              },
            ]
          "
          :total-mentors="totalMentors"
        />

        <template v-else-if="modalState && modalState.type === 'other'">
          <div class="bg-secondary-500 grid place-items-center rounded-xl p-4">
            <component :is="modalState.icon" class="size-8 text-white" />
          </div>
          <div class="mt-6 text-center text-xl font-semibold">
            {{ modalState.header }}
          </div>
          <div class="mt-1 text-center text-neutral-600">
            {{ modalState.description }}
          </div>
        </template>

        <template v-else>
          <div class="bg-secondary-500 grid place-items-center rounded-xl p-4">
            <LockIcon class="size-8 text-white" />
          </div>
          <div class="mt-6 text-center text-xl font-semibold">
            Unlock full access to GrowthMentor
          </div>
          <div class="mt-1 text-center text-neutral-600">
            With the Pro plan you can use all the features of GrowthMentor.
          </div>
        </template>

        <!-- Where the person is in the slides of the account pop-up. -->
        <div
          v-if="modalState?.type === 'chat-account' && accountStep > 1"
          class="mt-6 flex items-center gap-1.5"
          aria-hidden="true"
        >
          <span
            v-for="slide in accountSlides"
            :key="slide"
            class="size-1.5 rounded-full"
            :class="slide === accountStep ? 'bg-neutral-700' : 'bg-neutral-300'"
          />
        </div>
        <BaseButton
          @click="handleGetStartedClick"
          color="purple"
          size="large"
          is-block
          :class="
            modalState?.type === 'chat-account' && accountStep > 1
              ? 'mt-4'
              : 'mt-6'
          "
        >
          {{ buttonLabel }}
        </BaseButton>
        <button
          v-if="
            modalState?.type === 'chat-account' &&
            accountStep > 1 &&
            accountStep < lastSlide
          "
          type="button"
          class="mt-3 text-sm font-medium text-neutral-500 hover:text-neutral-700"
          @click="goToSignUp"
        >
          Skip and create my account
        </button>
        <div v-if="!isUserLoggedIn" class="mt-4 text-xs text-neutral-600">
          Already a member?
          <InertiaLink href="/users/sign_in" class="text-secondary-500">
            Log in
          </InertiaLink>
        </div>
      </div>
    </div>

    <button
      class="absolute top-4 right-4 z-10 -m-2 rounded-full bg-white/85 p-2 transition-colors hover:bg-neutral-50"
      @click="closeModal(UNLOCK_FULL_ACCESS_MODAL_NAME)"
    >
      <CloseIcon class="size-4 text-neutral-400" />
    </button>
  </BaseModal>
</template>

<script lang="ts">
export type UnlockFullAccessModalState =
  | {
      type: 'request-a-call'
      firstName: string
      avatarUrl: string
    }
  | {
      type: 'other'
      header: string
      description: string
      icon: string
    }
  | {
      type: 'roadmap'
    }
  | {
      // A visitor's click on a card of the mentor chat, or on a button of a
      // mentor profile (with `action`): the account pop-up and its slides.
      type: 'chat-account'
      firstName: string
      avatarUrl: string
      // The button of a mentor profile the visitor clicked. None: a chat card,
      // whose request the chat already wrote.
      action?: 'request' | 'message' | 'list'
      // The mentor's full name and title, for the slide on unlimited calls.
      mentorName?: string
      mentorTitle?: string | null
      // The mentors of that row, the clicked one first, for the slide on
      // unlimited calls.
      mentors?: {
        id: number
        name: string
        title: string | null
        avatarUrl: string | null
      }[]
    }
  | {
      // A card of the mentor chat: the plans, for the mentor of that card.
      type: 'chat'
      firstName: string
      // The mentor's photo for the strip about the written request.
      avatarUrl?: string
      // Called when Stripe checkout opens from this pop-up.
      onCheckout?: () => void
    }
</script>

<script setup lang="ts">
import CloseIcon from '~icons/lucide/x'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import { computed, ref, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import { useModals } from '@/shared/composables/useModals.js'
import BaseModal from '@/shared/components/BaseModal.vue'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import BillingTermCard from '@/modules/site/components/BillingTermCard.vue'
import type { PersonalPlanProp } from '@/modules/site/utils/personalPlans'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import LockIcon from '~icons/lucide/lock'
import ChatAccountSlide from '@/modules/mentor-chat/components/ChatAccountSlide.vue'

const emit = defineEmits(['hide', 'show'])

const { isUserLoggedIn } = usePermissionSystem()

const { closeModal, getModalState } = useModals()
const page = usePage()

const modalState = computed(
  () =>
    getModalState(
      UNLOCK_FULL_ACCESS_MODAL_NAME,
    ) as UnlockFullAccessModalState | null,
)

type UnlockModalData = {
  totalMentors: number
  personalPlans?: PersonalPlanProp[]
  openCheckoutSessionPath?: string
  successPath?: string
}

const unlockModalData = computed(
  () =>
    page.props.shared.unlockFullAccessModalData as UnlockModalData | undefined,
)

// The Roadmap and the mentor chat show the plans in the pop-up itself.
const isPlansModal = computed(
  () =>
    modalState.value?.type === 'roadmap' || modalState.value?.type === 'chat',
)
const modalWidthClass = computed(() => {
  if (isPlansModal.value) return 'max-w-2xl relative'
  return accountStep.value > 1 ? 'max-w-lg relative' : 'max-w-md relative'
})

// The account pop-up of a chat card has slides after its first screen
// (ChatAccountSlide): 2 real calls, 3 unlimited calls with the mentors of
// the row, 4 the wall of mentor faces. Each new pop-up starts on the first.
// The film, unlimited calls, the faces: for a chat card and for a mentor
// profile or a Browse card alike.
const accountSlides = computed(() => [2, 3, 4])
const lastSlide = computed(() => accountSlides.value.at(-1) ?? 4)
const accountStep = ref(1)
watch(modalState, () => (accountStep.value = 1))

const buttonLabel = computed(() => {
  if (isUserLoggedIn.value) return 'Upgrade to Pro membership'
  const onSlides =
    modalState.value?.type === 'chat-account' && accountStep.value > 1
  return onSlides && accountStep.value < lastSlide.value
    ? 'Next'
    : 'Create account'
})

const totalMentors = computed(() => {
  return unlockModalData.value?.totalMentors ?? 0
})

const plansLine = computed(() => {
  const state = modalState.value
  const who =
    state?.type === 'chat' ? state.firstName : 'the mentors on your roadmap'
  return `Book ${who} and ${totalMentors.value}+ more across GrowthMentor.`
})

const roadmapBenefits = [
  'Unlimited 1:1 calls',
  'AI mentor matching',
  'Recordings and recaps',
]

// The page that opened the pop-up can ask to hear that checkout opens (the
// mentor chat does, for its staff page). A fault there never stops the way
// to Stripe.
const noteCheckout = () => {
  const state = modalState.value
  if (state?.type !== 'chat') return
  try {
    state.onCheckout?.()
  } catch {
    // Nothing to do: the person goes on to Stripe.
  }
}

const purchasing = ref(false)
const checkout = async (planId?: number) => {
  const path = unlockModalData.value?.openCheckoutSessionPath
  const successPath = unlockModalData.value?.successPath
  if (purchasing.value || !planId || !path || !successPath) return

  try {
    purchasing.value = true
    const { data } = await axios.post(path, {
      plan_id: planId,
      success_path: successPath,
    })
    if (data.checkout_session_url) {
      noteCheckout()
      window.location.href = data.checkout_session_url
    }
  } catch {
    notify({
      type: 'error',
      text: 'Something went wrong starting your membership. Please try again.',
    })
  } finally {
    purchasing.value = false
  }
}

// A visitor from a mentor profile has no chat to go back to: the classic
// door (the form, the wizard, the plans). Without it the sign-up URL opens
// the mentor chat.
const signUpPath = computed(() => {
  const state = modalState.value
  return state?.type === 'chat-account' && state.action
    ? '/mentees/sign_up?flow=classic'
    : '/mentees/sign_up'
})
const goToSignUp = () => router.visit(signUpPath.value)

// The first screen of the pop-up on a mentor profile, by the button.
const ACTION_TITLES = {
  request: 'Book a call with',
  message: 'Message',
  list: 'Save',
} as const

const handleGetStartedClick = () => {
  if (
    modalState.value?.type === 'chat-account' &&
    !isUserLoggedIn.value &&
    accountStep.value < lastSlide.value
  ) {
    const slides = [1, ...accountSlides.value]
    accountStep.value = slides[slides.indexOf(accountStep.value) + 1]
    return
  }
  if (isUserLoggedIn.value) {
    return router.visit('/upgrade_plan/new')
  } else {
    return goToSignUp()
  }
}
</script>
